/**
 * LayerPanel — Animated sidebar with layer list, visibility toggle,
 * color picker, drag-reorder, and expand/collapse.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import bbox from '@turf/bbox';
import {
  Eye, EyeOff, Trash2, Plus, ChevronDown, ChevronRight,
  Layers, GripVertical, Lock, Unlock, Link2, Landmark, MapPin,
  Tractor, Mountain, Zap, Truck, Building2, Sparkles, Edit3, Save,
} from 'lucide-react';
import { useMapLayers, useCreateLayer, useUpdateLayer, useDeleteLayer, useMapParcelsByIds, fetchMapParcelById } from '../api';
import { useMapStore } from '../MapContext';
import { MAPPER_BOUNDARY_ZOOM_THRESHOLD } from '../constants';
import { LAYER_TYPE_ICONS, LAYER_TYPE_COLORS, DEFAULT_LAYER_COLORS, ALL_CATEGORIES, CATEGORY_COLORS, CATEGORY_ICONS, CATEGORY_LABELS } from '../types';
import type { MapLayer, MapParcel, LayerType, InfraCategoryType, RenderedParcelSummary } from '../types';
import { useRenderedParcelSummaries } from '../useRenderedParcelSummaries';
import FarmChecklistPanel from './FarmChecklistPanel';
import LandUnitsPanel from './LandUnitsPanel';
import AnalysisOverlayPanel from './AnalysisOverlayPanel';
import { useMapperPluginConfig, landUnitLabel } from '../mapperConfig';

const BOUNDARY_ZOOM_THRESHOLD = MAPPER_BOUNDARY_ZOOM_THRESHOLD;

/* ------------------------------------------------------------------ */
/*  Color Picker inline                                                 */
/* ------------------------------------------------------------------ */

function InlineColorPicker({ color, onChange }: { color: string; onChange: (c: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="w-5 h-5 rounded-full border-2 border-white shadow-sm ring-1 ring-gray-200 transition-transform hover:scale-110 active:scale-95"
        style={{ backgroundColor: color }}
      />
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -4 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            className="absolute left-0 top-7 z-50 bg-white rounded-lg shadow-xl border p-2 grid grid-cols-5 gap-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            {DEFAULT_LAYER_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { onChange(c); setOpen(false); }}
                className="w-6 h-6 rounded-full border-2 transition-all hover:scale-125 active:scale-95"
                style={{
                  backgroundColor: c,
                  borderColor: c === color ? '#1f2937' : 'transparent',
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  New Layer Dialog                                                    */
/* ------------------------------------------------------------------ */

function NewLayerForm({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState<LayerType>('custom');
  const createLayer = useCreateLayer();

  const handleCreate = () => {
    if (!name.trim()) return;
    createLayer.mutate({
      name: name.trim(),
      layer_type: type,
      style: { color: DEFAULT_LAYER_COLORS[Math.floor(Math.random() * DEFAULT_LAYER_COLORS.length)], fillOpacity: 0.25, weight: 2 },
    });
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="overflow-hidden"
    >
      <div className="p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300 space-y-2">
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder="Layer name..."
          className="w-full text-sm px-3 py-2 rounded-md border border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none transition-colors"
        />
        <div className="flex gap-1.5">
          {(['boundary', 'infrastructure', 'custom'] as LayerType[]).map((t) => {
            const colors = LAYER_TYPE_COLORS[t];
            return (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`text-xs px-2 py-1 rounded-full font-medium transition-all ${
                  type === t ? `${colors.bg} ${colors.text}` : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleCreate}
            disabled={!name.trim()}
            className="flex-1 text-sm font-medium py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 transition-all active:scale-[0.98]"
          >
            Create
          </button>
          <button
            onClick={onClose}
            className="text-sm font-medium py-1.5 px-3 rounded-md text-gray-500 hover:bg-gray-200 transition-all active:scale-[0.98]"
          >
            Cancel
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function getParcelListLabel(parcel: Pick<MapParcel, 'id' | 'feature_name' | 'farm_name' | 'external_id' | 'sg_number' | 'erf_number' | 'prcl_key' | 'natural_key_value' | 'land_type'> | RenderedParcelSummary) {
  const featureName = String(parcel.feature_name || '').trim();
  const farmName = String(parcel.farm_name || '').trim();
  const externalId = String(parcel.external_id || parcel.natural_key_value || '').trim();
  const sgNumber = String(parcel.sg_number || '').trim();
  const erfNumber = String(parcel.erf_number || '').trim();
  const parcelKey = String(parcel.prcl_key || '').trim();
  const landType = String(parcel.land_type || '').trim().toLowerCase();

  if (featureName) return featureName;
  if (erfNumber && (landType === 'erf' || !farmName)) return `Erf ${erfNumber}`;
  return farmName || externalId || (erfNumber ? `Erf ${erfNumber}` : sgNumber || parcelKey || parcel.id);
}

/* ------------------------------------------------------------------ */
/*  Layer Item                                                          */
/* ------------------------------------------------------------------ */

function LayerItem({ layer, isSelected }: { layer: MapLayer; isSelected: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(layer.name);
  const [descriptionDraft, setDescriptionDraft] = useState(layer.description || '');
  const updateLayer = useUpdateLayer();
  const deleteLayer = useDeleteLayer();
  const { setSelectedLayer } = useMapStore();
  const Icon = LAYER_TYPE_ICONS[layer.layer_type] || Layers;
  const typeColor = LAYER_TYPE_COLORS[layer.layer_type] || LAYER_TYPE_COLORS.custom;

  useEffect(() => {
    setNameDraft(layer.name);
    setDescriptionDraft(layer.description || '');
  }, [layer.description, layer.name]);

  const toggleVisibility = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    updateLayer.mutate({ id: layer.id, visible: !layer.visible });
  }, [layer.id, layer.visible, updateLayer]);

  const handleColorChange = useCallback((color: string) => {
    updateLayer.mutate({ id: layer.id, style: { ...layer.style, color } });
  }, [layer.id, layer.style, updateLayer]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(`Delete layer "${layer.name}" and all its features?`)) {
      deleteLayer.mutate(layer.id);
    }
  }, [layer.id, layer.name, deleteLayer]);

  const toggleLock = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    updateLayer.mutate({ id: layer.id, locked: !layer.locked });
  }, [layer.id, layer.locked, updateLayer]);

  const handleSaveDetails = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    const trimmedName = nameDraft.trim();
    if (!trimmedName) return;
    updateLayer.mutate({
      id: layer.id,
      name: trimmedName,
      description: descriptionDraft.trim(),
    });
    setEditing(false);
  }, [descriptionDraft, layer.id, nameDraft, updateLayer]);

  return (
    <Reorder.Item
      value={layer}
      id={layer.id}
      className="select-none"
      whileDrag={{ scale: 1.02, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 50 }}
    >
      <motion.div
        layout
        onClick={() => setSelectedLayer(isSelected ? null : layer)}
        className={`
          group rounded-lg border transition-colors cursor-pointer
          ${isSelected
            ? 'border-blue-300 bg-blue-50/50'
            : 'border-transparent hover:bg-gray-50'
          }
        `}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-2 py-2">
          <GripVertical className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />

          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <motion.div animate={{ rotate: expanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
              <ChevronDown className="w-3.5 h-3.5" />
            </motion.div>
          </button>

          <InlineColorPicker color={layer.style.color} onChange={handleColorChange} />

          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-800 truncate">{layer.name}</div>
            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeColor.bg} ${typeColor.text}`}>
                {layer.layer_type}
              </span>
              <span className="text-[10px] text-gray-400">{layer.feature_count} features</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {layer.linked_entity_id && (
              <Link2 className="w-3.5 h-3.5 text-blue-400" />
            )}
            <motion.button
              whileTap={{ scale: 0.85 }}
              onClick={toggleLock}
              className="p-1 rounded-md hover:bg-gray-200 transition-colors"
              title={layer.locked ? 'Unlock layer' : 'Lock layer'}
            >
              {layer.locked ? (
                <Lock className="w-3.5 h-3.5 text-amber-500" />
              ) : (
                <Unlock className="w-3.5 h-3.5 text-gray-400" />
              )}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.85 }}
              onClick={toggleVisibility}
              className="p-1 rounded-md hover:bg-gray-200 transition-colors"
            >
              {layer.visible ? (
                <Eye className="w-3.5 h-3.5 text-gray-500" />
              ) : (
                <EyeOff className="w-3.5 h-3.5 text-gray-300" />
              )}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.85 }}
              onClick={handleDelete}
              className="p-1 rounded-md hover:bg-red-100 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500 transition-colors" />
            </motion.button>
          </div>
        </div>

        {/* Expanded details */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="overflow-hidden"
            >
              <div className="px-3 pb-3 pt-1 text-xs text-gray-500 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
                      {layer.locked ? 'Locked' : 'Editable'}
                    </span>
                    {layer.linked_entity_id && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">
                        Linked
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing((current) => !current);
                    }}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    {editing ? 'Close' : 'Edit'}
                  </button>
                </div>

                {editing ? (
                  <div className="space-y-2">
                    <input
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveDetails()}
                      placeholder="Layer name"
                      className="w-full rounded-md border border-gray-200 px-2.5 py-2 text-xs text-gray-700 outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                    />
                    <textarea
                      value={descriptionDraft}
                      onChange={(e) => setDescriptionDraft(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      rows={3}
                      placeholder="Describe what this layer contains"
                      className="w-full rounded-md border border-gray-200 px-2.5 py-2 text-xs text-gray-700 outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSaveDetails}
                        disabled={!nameDraft.trim()}
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-40"
                      >
                        <Save className="w-3.5 h-3.5" />
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditing(false);
                          setNameDraft(layer.name);
                          setDescriptionDraft(layer.description || '');
                        }}
                        className="rounded-md px-2.5 py-1.5 text-[11px] font-medium text-gray-500 hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {layer.description ? <p>{layer.description}</p> : <p className="italic text-gray-400">No description yet</p>}
                    <p>Source: {layer.source}</p>
                    <p>Updated: {new Date(layer.metadata.updated_at).toLocaleDateString()}</p>
                    <p>Created: {new Date(layer.metadata.created_at).toLocaleDateString()}</p>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </Reorder.Item>
  );
}

/* ------------------------------------------------------------------ */
/*  Parcel Section                                                      */
/* ------------------------------------------------------------------ */

function ParcelSection() {
  const PANEL_PARCEL_DISPLAY_LIMIT = 50;
  const queryClient = useQueryClient();
  const {
    activeParcel,
    activeFarmGroup,
    selectedParcels,
    toggleParcelSelection,
    triggerFlyTo,
    showBoundaries,
    setShowBoundaries,
    parcelLayerScope,
    setParcelLayerScope,
    guidedParcelSelectionActive,
    parcelViewportBbox,
    parcelViewportZoom,
  } = useMapStore();
  const farmOnlyMode = parcelLayerScope === 'farm_only' && Boolean(activeFarmGroup?.parcel_ids?.length);
  const viewportReady = !!parcelViewportBbox && parcelViewportZoom >= BOUNDARY_ZOOM_THRESHOLD;
  const groupParcels = useMapParcelsByIds(activeFarmGroup?.parcel_ids || [], { enabled: farmOnlyMode });
  const renderedViewportParcels = useRenderedParcelSummaries({
    enabled: !farmOnlyMode && !guidedParcelSelectionActive && viewportReady && showBoundaries,
    limit: PANEL_PARCEL_DISPLAY_LIMIT,
  });
  const knownParcelsById = useMemo(() => {
    const knownParcels = [...(groupParcels.data ?? []), ...selectedParcels];
    return new Map(knownParcels.map((parcel) => [parcel.id, parcel]));
  }, [groupParcels.data, selectedParcels]);
  const parcels = useMemo(() => {
    const visibleParcels = farmOnlyMode
      ? (groupParcels.data ?? [])
      : (showBoundaries ? renderedViewportParcels.parcels : []);
    const selectedIds = new Set(visibleParcels.map((parcel) => parcel.id));
    const selectedOutsideViewport = selectedParcels.filter((parcel) => !selectedIds.has(parcel.id));
    return selectedOutsideViewport.length > 0 ? [...selectedOutsideViewport, ...visibleParcels] : visibleParcels;
  }, [farmOnlyMode, groupParcels.data, renderedViewportParcels.parcels, selectedParcels, showBoundaries]);
  const selectedParcelIds = useMemo(
    () => new Set(selectedParcels.map((parcel) => parcel.id)),
    [selectedParcels]
  );
  const visibleParcelList = useMemo(
    () => parcels.slice(0, PANEL_PARCEL_DISPLAY_LIMIT),
    [parcels]
  );
  const parcelCount = farmOnlyMode ? parcels.length : Math.max(parcels.length, renderedViewportParcels.total);
  const [expanded, setExpanded] = useState(true);

  const handleParcelClick = useCallback(async (parcel: MapParcel | RenderedParcelSummary, isSelected: boolean) => {
    const existing = knownParcelsById.get(parcel.id) || null;
    const resolvedParcel = existing || await queryClient.fetchQuery({
      queryKey: ['mapper', 'parcels', 'detail', parcel.id],
      queryFn: ({ signal }) => fetchMapParcelById(parcel.id, signal),
      staleTime: 15_000,
    }).catch(() => null);

    if (!resolvedParcel) return;

    toggleParcelSelection(resolvedParcel);
    if (!isSelected && resolvedParcel.geometry) {
      try {
        const b = bbox({ type: 'Feature', geometry: resolvedParcel.geometry, properties: {} });
        triggerFlyTo({ center: [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2], bounds: b });
      } catch {
        // ignore bounds errors for malformed shapes
      }
    }
  }, [knownParcelsById, queryClient, toggleParcelSelection, triggerFlyTo]);

  if (guidedParcelSelectionActive) return null;

  return (
    <div className="border-b border-gray-100">
      <div className="flex items-center gap-2 px-4 py-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 flex-1 hover:bg-gray-50 transition-colors rounded -ml-1 pl-1"
        >
          <Landmark className="w-3.5 h-3.5 text-teal-600" />
          <span className="text-xs font-semibold text-gray-700 flex-1 text-left">
            {farmOnlyMode
              ? activeFarmGroup?.name || 'Focused selection'
              : !showBoundaries && !activeParcel
              ? 'Parcel boundaries are hidden'
              : activeParcel && !viewportReady
              ? 'Selected parcel'
              : viewportReady
                ? 'Parcels in this view'
                : 'Zoom in for parcel boundaries'}
          </span>
          {parcelCount > 0 && (
            <span className="text-[10px] bg-teal-100 text-teal-600 px-1.5 py-0.5 rounded-full font-medium">{parcelCount}</span>
          )}
          <motion.div animate={{ rotate: expanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="w-3 h-3 text-gray-400" />
          </motion.div>
        </button>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => {
            if (activeFarmGroup?.parcel_ids?.length) {
              if (farmOnlyMode) {
                setParcelLayerScope('viewport');
                setShowBoundaries(true);
              } else {
                setParcelLayerScope('farm_only');
              }
              return;
            }
            setShowBoundaries(!showBoundaries);
          }}
          title={
            activeFarmGroup?.parcel_ids?.length
              ? (farmOnlyMode ? 'Show surrounding cadastral boundaries' : 'Focus on this selection only')
              : (showBoundaries ? 'Hide cadastral boundaries' : 'Show cadastral boundaries')
          }
          className={`text-[10px] px-2 py-1 rounded-full font-medium transition-all flex items-center gap-1 ${
            (activeFarmGroup?.parcel_ids?.length ? farmOnlyMode : showBoundaries)
              ? 'bg-teal-100 text-teal-700 ring-1 ring-teal-300'
              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          }`}
        >
          {(activeFarmGroup?.parcel_ids?.length ? farmOnlyMode : showBoundaries) ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          {activeFarmGroup?.parcel_ids?.length ? (farmOnlyMode ? 'Focused' : 'Surroundings') : 'Boundaries'}
        </motion.button>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-2 space-y-0.5">
              {parcels.length === 0 ? (
                <div className="rounded-md bg-gray-50 px-2.5 py-2 text-[11px] text-gray-400">
                  {renderedViewportParcels.isLoading && !farmOnlyMode
                    ? 'Loading parcel boundaries in this map area...'
                    : farmOnlyMode
                    ? 'This selection has no parcel members yet.'
                    : !showBoundaries && !activeParcel
                    ? 'Parcel boundaries are hidden. Toggle boundaries above to pick a parcel from the map.'
                    : viewportReady
                    ? 'No parcel boundaries are visible in this map area yet.'
                    : 'Zoom in until the parcel boundaries appear, then click the parcel you want.'}
                </div>
              ) : (
                visibleParcelList.map((parcel) => {
                  const isActive = activeParcel?.id === parcel.id;
                  const isSelected = selectedParcelIds.has(parcel.id);
                  return (
                    <motion.button
                      key={parcel.id}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => { void handleParcelClick(parcel, isSelected); }}
                      className={`
                        w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors
                        ${isActive
                          ? 'bg-teal-50 border border-teal-200'
                          : isSelected
                            ? 'bg-cyan-50 border border-cyan-200'
                            : 'hover:bg-gray-50 border border-transparent'}
                      `}
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? 'bg-teal-500' : isSelected ? 'bg-cyan-500' : 'bg-gray-300'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-800 truncate">{getParcelListLabel(parcel)}</div>
                        <div className="text-[10px] text-gray-400">{parcel.area_ha.toFixed(1)} ha</div>
                      </div>
                      {parcel.linked_entity_id && (
                        <Link2 className="w-3 h-3 text-blue-400 flex-shrink-0" />
                      )}
                    </motion.button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Category selector (function-based, v2)                              */
/* ------------------------------------------------------------------ */

function CategorySelector() {
  const { activeCategory, setActiveCategory } = useMapStore();

  return (
    <div className="px-3 py-2 border-b border-gray-100 flex gap-1 flex-wrap">
      {/* "All" button */}
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={() => setActiveCategory(null)}
        className={`
          text-[10px] px-1.5 py-1 rounded-full font-medium flex items-center gap-0.5 transition-all
          ${activeCategory === null ? 'bg-gray-200 text-gray-700 ring-1 ring-gray-400' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}
        `}
      >
        All
      </motion.button>
      {ALL_CATEGORIES.map(cat => {
        const clr = CATEGORY_COLORS[cat];
        const IC = CATEGORY_ICONS[cat];
        const active = activeCategory === cat;
        return (
          <motion.button
            key={cat}
            whileTap={{ scale: 0.9 }}
            onClick={() => setActiveCategory(cat)}
            title={CATEGORY_LABELS[cat]}
            className={`
              text-[10px] px-1.5 py-1 rounded-full font-medium flex items-center gap-0.5 transition-all
              ${active ? `${clr.bg} ${clr.text} ring-1 ring-current` : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}
            `}
          >
            <IC className="w-3 h-3" />
            <span className="hidden sm:inline">{CATEGORY_LABELS[cat]?.split(' ')[0]}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Panel                                                          */
/* ------------------------------------------------------------------ */

export default function LayerPanel({ onStartWizard }: { onStartWizard?: () => void }) {
  const [showNewForm, setShowNewForm] = useState(false);
  const { selectedLayer, layerPanelOpen, activeParcel, activeFarmGroup } = useMapStore();
  const { data: configData } = useMapperPluginConfig();
  const landUnitPlural = landUnitLabel(configData?.config, true);
  const { data } = useMapLayers({ enabled: true });
  const layers = data?.layers ?? [];

  // For reorder (visual only — we don't persist order yet)
  const [orderedLayers, setOrderedLayers] = useState<MapLayer[]>([]);
  const displayLayers = orderedLayers.length === layers.length ? orderedLayers : layers;

  return (
    <AnimatePresence>
      {layerPanelOpen && (
        <motion.div
          initial={{ x: -320, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -320, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="w-full bg-white flex flex-col h-full z-20"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {activeParcel ? (
                  <Layers className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Landmark className="w-4 h-4 text-emerald-600" />
                )}
                <span className="text-sm font-semibold text-gray-800">{activeParcel ? 'Object layers' : 'Workspace'}</span>
                {activeParcel && (
                  <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-medium">
                    {layers.length}
                  </span>
                )}
              </div>
              {activeParcel && (
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowNewForm(!showNewForm)}
                  className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </motion.button>
              )}
            </div>
          </div>

          {onStartWizard && (
            <div className="px-3 pt-3">
              <div className="rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50 p-3">
                <div className="flex items-center gap-2 text-teal-800">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em]">Getting started</span>
                </div>
                <p className="mt-2 text-sm font-medium text-gray-900">
                  Start with a legal entity, choose the land parcel, then scan for infrastructure.
                </p>
                <button
                  type="button"
                  onClick={onStartWizard}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-teal-700"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Start Setup Wizard
                </button>
              </div>
            </div>
          )}

          {/* Parcels (properties) section */}
          <ParcelSection />

          <LandUnitsPanel />
          <AnalysisOverlayPanel />

          {activeFarmGroup && <FarmChecklistPanel />}

          <>
            {!activeParcel && (
              <div className="px-3 py-2 text-[11px] text-slate-500">
                Select cadastral parcels or create {landUnitPlural.toLowerCase()} — all tools are available.
              </div>
            )}
            {/* Category selector */}
            <CategorySelector />

              {/* New layer form */}
              <div className="px-3 pt-2">
                <AnimatePresence>
                  {showNewForm && <NewLayerForm onClose={() => setShowNewForm(false)} />}
                </AnimatePresence>
              </div>

              {/* Layer list */}
              <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
                {layers.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center justify-center py-12 text-center"
                  >
                    <Layers className="w-10 h-10 text-gray-200 mb-3" />
                    <p className="text-sm font-medium text-gray-400">No object layers yet</p>
                    <p className="text-xs text-gray-300 mt-1">
                      Draw an object layer or import an object-layer file for this parcel.
                    </p>
                  </motion.div>
                ) : (
                  <Reorder.Group
                    axis="y"
                    values={displayLayers}
                    onReorder={setOrderedLayers}
                    className="space-y-0.5"
                  >
                    {displayLayers.map((layer) => (
                      <LayerItem
                        key={layer.id}
                        layer={layer}
                        isSelected={selectedLayer?.id === layer.id}
                      />
                    ))}
                  </Reorder.Group>
                )}
              </div>
            </>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
